import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ChatIcon from '@mui/icons-material/Chat';
import MinimizeIcon from '@mui/icons-material/Minimize';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SearchIcon from '@mui/icons-material/Search';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import InputAdornment from '@mui/material/InputAdornment';
import { Rnd } from 'react-rnd';
import { useAppAuth } from '../auth/appAuth';
import PostCard from './PostCard';
import QuotaBar from './QuotaBar';
import { useChatContext } from '../context/ChatContext';

// API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_SERVER_URL || 'https://127.0.0.1:5005';

// Dev auth bypass flag
const DEV_BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';

// SSE Event types from backend
interface StreamEvent {
  type: 'status' | 'chunk' | 'done' | 'error';
  message?: string;
  content?: string;
}

// Conversation types
interface Conversation {
  id: string;
  org_id: number;
  user_id: number;
  title: string | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
}

interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  is_complete?: boolean;
}

/**
 * Parse post blocks from text
 * Detects patterns like:
 * [POST brand="X" platform="Y" date="Z" engagement="N" url="..."]Message[/POST]
 */
interface ParsedPost {
  brand: string;
  platform: string;
  message: string;
  date?: string;
  engagement?: string;
  url?: string;
}

const parsePostBlocks = (text: string): { posts: ParsedPost[]; cleanedText: string } => {
  const posts: ParsedPost[] = [];
  const postRegex = /\[POST\s+([^\]]+)\]([\s\S]*?)\[\/POST\]/g;
  
  let cleanedText = text.replace(postRegex, (match, attributes, message) => {
    const post: ParsedPost = {
      brand: '',
      platform: '',
      message: message.trim(),
    };
    
    // Parse attributes
    const attrRegex = /(\w+)="([^"]*)"/g;
    let attrMatch;
    while ((attrMatch = attrRegex.exec(attributes)) !== null) {
      const [, key, value] = attrMatch;
      if (key === 'brand') post.brand = value;
      else if (key === 'platform') post.platform = value;
      else if (key === 'date') post.date = value;
      else if (key === 'engagement') post.engagement = value;
      else if (key === 'url') post.url = value;
    }
    
    if (post.brand && post.platform) {
      posts.push(post);
      return `__POST_${posts.length - 1}__`;
    }
    return match;
  });
  
  return { posts, cleanedText };
};

/**
 * Parse markdown tables from text
 * Detects patterns like:
 * | Header 1 | Header 2 |
 * |----------|----------|
 * | Cell 1   | Cell 2   |
 */
interface ParsedTable {
  headers: string[];
  rows: string[][];
  alignments: ('left' | 'center' | 'right')[];
}

const parseMarkdownTables = (text: string): { tables: ParsedTable[]; cleanedText: string } => {
  const tables: ParsedTable[] = [];
  
  // Regex to match markdown tables (header row, separator row, data rows)
  const tableRegex = /(?:^|\n)((?:\|[^\n]+\|[ \t]*\n)+)/gm;
  
  let cleanedText = text.replace(tableRegex, (match, tableBlock) => {
    const lines = tableBlock.trim().split('\n').filter((line: string) => line.trim());
    
    if (lines.length < 2) return match; // Need at least header and separator
    
    // Parse each line into cells
    const parseRow = (line: string): string[] => {
      // Remove leading/trailing pipes and split by |
      return line.replace(/^\||\|$/g, '').split('|').map((cell: string) => cell.trim());
    };
    
    const headerLine = lines[0];
    const separatorLine = lines[1];
    
    // Verify separator line (should have dashes, optionally colons for alignment)
    if (!/^[\s|:-]+$/.test(separatorLine)) return match;
    
    const headers = parseRow(headerLine);
    const separatorCells = parseRow(separatorLine);
    
    // Parse alignments from separator
    const alignments: ('left' | 'center' | 'right')[] = separatorCells.map((sep: string) => {
      const trimmed = sep.trim();
      if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center';
      if (trimmed.endsWith(':')) return 'right';
      return 'left';
    });
    
    // Parse data rows
    const rows: string[][] = [];
    for (let i = 2; i < lines.length; i++) {
      rows.push(parseRow(lines[i]));
    }
    
    if (headers.length > 0 && rows.length > 0) {
      tables.push({ headers, rows, alignments });
      return `\n__TABLE_${tables.length - 1}__\n`;
    }
    
    return match;
  });
  
  return { tables, cleanedText };
};

/**
 * Format inline markdown: **bold**, *italic*
 * Used by both table cells and main text formatting
 */
const formatInlineMarkdown = (text: string, keyPrefix: string = ''): React.ReactNode => {
  const result: React.ReactNode[] = [];
  const inlineRegex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIdx = 0;
  let match;
  let key = 0;

  while ((match = inlineRegex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIdx) {
      result.push(text.slice(lastIdx, match.index));
    }

    if (match[2]) {
      // **bold**
      result.push(<strong key={`${keyPrefix}b-${key++}`}>{match[2]}</strong>);
    } else if (match[3]) {
      // *italic*
      result.push(<em key={`${keyPrefix}i-${key++}`}>{match[3]}</em>);
    }

    lastIdx = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIdx < text.length) {
    result.push(text.slice(lastIdx));
  }

  return result.length > 0 ? <>{result}</> : text;
};

/**
 * Render a markdown table as a styled React component
 */
const TableRenderer: React.FC<{ table: ParsedTable }> = ({ table }) => {
  const getAlignment = (index: number): 'left' | 'center' | 'right' => {
    return table.alignments[index] || 'left';
  };

  // Format cell content with inline markdown and color coding
  const formatCell = (cell: string, keyPrefix: string): React.ReactNode => {
    // First check for +/- number patterns for color coding
    const trimmed = cell.trim();
    if (trimmed.startsWith('+') && /^\+[\d.]/.test(trimmed)) {
      return <span style={{ color: '#4ade80' }}>{formatInlineMarkdown(cell, keyPrefix)}</span>;
    }
    if (trimmed.startsWith('-') && /^-[\d.]/.test(trimmed)) {
      return <span style={{ color: '#f87171' }}>{formatInlineMarkdown(cell, keyPrefix)}</span>;
    }
    return formatInlineMarkdown(cell, keyPrefix);
  };

  // Sticky column styles
  const stickyColumnSx = {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: 'inherit',
    // Solid background for sticky column to hide scrolling content
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '1px',
      background: 'rgba(199, 125, 219, 0.2)',
    },
  };

  return (
    <Box
      sx={{
        margin: '12px 0',
        overflowX: 'auto',
        maxWidth: '100%',
        borderRadius: '8px',
        border: '1px solid rgba(199, 125, 219, 0.2)',
        background: 'rgba(0, 0, 0, 0.3)',
        // Custom scrollbar for narrow chat
        '&::-webkit-scrollbar': {
          height: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(199, 125, 219, 0.4)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(199, 125, 219, 0.6)',
          },
        },
      }}
    >
      <Box
        component="table"
        sx={{
          width: '100%',
          minWidth: 'max-content',
          borderCollapse: 'collapse',
          fontSize: '0.8rem',
        }}
      >
        <Box component="thead">
          <Box
            component="tr"
            sx={{
              background: 'rgba(199, 125, 219, 0.15)',
            }}
          >
            {table.headers.map((header, idx) => (
              <Box
                component="th"
                key={idx}
                sx={{
                  padding: '8px 12px',
                  textAlign: getAlignment(idx),
                  fontWeight: 600,
                  color: '#C77DDB',
                  borderBottom: '1px solid rgba(199, 125, 219, 0.3)',
                  whiteSpace: 'nowrap',
                  // Make first column sticky
                  ...(idx === 0 ? {
                    ...stickyColumnSx,
                    background: 'rgba(30, 10, 40, 0.98)',
                  } : {}),
                }}
              >
                {formatInlineMarkdown(header, `h${idx}-`)}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {table.rows.map((row, rowIdx) => {
            const isEven = rowIdx % 2 === 1;
            const rowBg = isEven ? 'rgba(20, 8, 30, 0.98)' : 'rgba(15, 5, 22, 0.98)';
            
            return (
              <Box
                component="tr"
                key={rowIdx}
                sx={{
                  background: rowBg,
                  '&:hover': {
                    background: 'rgba(199, 125, 219, 0.12)',
                    '& td:first-of-type': {
                      background: 'rgba(50, 20, 70, 0.98)',
                    },
                  },
                }}
              >
                {row.map((cell, cellIdx) => (
                  <Box
                    component="td"
                    key={cellIdx}
                    sx={{
                      padding: '6px 12px',
                      textAlign: getAlignment(cellIdx),
                      color: 'rgba(255, 255, 255, 0.9)',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      whiteSpace: 'nowrap',
                      // Make first column sticky with solid background
                      ...(cellIdx === 0 ? {
                        ...stickyColumnSx,
                        background: rowBg,
                      } : {}),
                    }}
                  >
                    {formatCell(cell, `r${rowIdx}c${cellIdx}-`)}
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Simple markdown formatter for chat messages
 * Supports: **bold**, *italic*, `code`, ```code blocks```, headers, lists, [POST] blocks, tables
 */
const formatMarkdown = (text: string): React.ReactNode => {
  // First, extract POST blocks
  const { posts, cleanedText: textAfterPosts } = parsePostBlocks(text);
  
  // Extract tables
  const { tables, cleanedText } = parseMarkdownTables(textAfterPosts);
  
  // Split by code blocks first (preserve them)
  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  const codeBlocks: { placeholder: string; lang: string; code: string }[] = [];
  let processedText = cleanedText.replace(codeBlockRegex, (match, lang, code) => {
    const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
    codeBlocks.push({ placeholder, lang: lang || '', code: code.trim() });
    return placeholder;
  });

  // Process inline formatting
  const formatInline = (line: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    // Match patterns: **bold**, *italic*, `code`
    const inlineRegex = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`)/g;
    let lastIdx = 0;
    let inlineMatch;
    let inlineKey = 0;

    while ((inlineMatch = inlineRegex.exec(line)) !== null) {
      // Add text before match
      if (inlineMatch.index > lastIdx) {
        result.push(line.slice(lastIdx, inlineMatch.index));
      }

      if (inlineMatch[2]) {
        // **bold**
        result.push(<strong key={`b-${inlineKey++}`}>{inlineMatch[2]}</strong>);
      } else if (inlineMatch[3]) {
        // *italic*
        result.push(<em key={`i-${inlineKey++}`}>{inlineMatch[3]}</em>);
      } else if (inlineMatch[4]) {
        // `code`
        result.push(
          <code
            key={`c-${inlineKey++}`}
            style={{
              backgroundColor: 'rgba(199, 125, 219, 0.2)',
              padding: '2px 6px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.85em',
            }}
          >
            {inlineMatch[4]}
          </code>
        );
      }

      lastIdx = inlineMatch.index + inlineMatch[0].length;
    }

    // Add remaining text
    if (lastIdx < line.length) {
      result.push(line.slice(lastIdx));
    }

    return result.length > 0 ? result : [line];
  };

  // Split by lines and process
  const lines = processedText.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let inList = false;

  lines.forEach((line, lineIndex) => {
    // Check for POST placeholder
    const postMatch = line.match(/__POST_(\d+)__/);
    if (postMatch) {
      // Flush list if active
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      
      const postIndex = parseInt(postMatch[1], 10);
      const post = posts[postIndex];
      if (post) {
        elements.push(
          <PostCard
            key={`post-${lineIndex}`}
            brand={post.brand}
            platform={post.platform}
            message={post.message}
            date={post.date}
            engagementScore={post.engagement}
            url={post.url}
          />
        );
      }
      return;
    }
    
    // Check for table placeholder
    const tableMatch = line.match(/__TABLE_(\d+)__/);
    if (tableMatch) {
      // Flush list if active
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      
      const tableIndex = parseInt(tableMatch[1], 10);
      const table = tables[tableIndex];
      if (table) {
        elements.push(
          <TableRenderer key={`table-${lineIndex}`} table={table} />
        );
      }
      return;
    }
    
    // Check for code block placeholder
    const codeBlockMatch = codeBlocks.find((cb) => line.includes(cb.placeholder));
    if (codeBlockMatch) {
      // Flush list if active
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }

      elements.push(
        <pre
          key={`pre-${lineIndex}`}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            padding: '12px',
            borderRadius: '6px',
            overflow: 'auto',
            margin: '8px 0',
            border: '1px solid rgba(199, 125, 219, 0.2)',
          }}
        >
          <code
            style={{
              fontFamily: 'monospace',
              fontSize: '0.85em',
              color: '#e0e0e0',
            }}
          >
            {codeBlockMatch.code}
          </code>
        </pre>
      );
      return;
    }

    // Headers
    if (line.startsWith('### ')) {
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      elements.push(
        <div key={`h3-${lineIndex}`} style={{ fontWeight: 600, fontSize: '1em', marginTop: '12px', marginBottom: '4px' }}>
          {formatInline(line.slice(4))}
        </div>
      );
      return;
    }
    if (line.startsWith('## ')) {
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      elements.push(
        <div key={`h2-${lineIndex}`} style={{ fontWeight: 700, fontSize: '1.1em', marginTop: '12px', marginBottom: '4px' }}>
          {formatInline(line.slice(3))}
        </div>
      );
      return;
    }
    if (line.startsWith('# ')) {
      if (inList && listItems.length > 0) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
      elements.push(
        <div key={`h1-${lineIndex}`} style={{ fontWeight: 700, fontSize: '1.2em', marginTop: '12px', marginBottom: '4px' }}>
          {formatInline(line.slice(2))}
        </div>
      );
      return;
    }

    // List items
    if (line.match(/^[\-\*]\s/)) {
      inList = true;
      listItems.push(<li key={`li-${lineIndex}`}>{formatInline(line.slice(2))}</li>);
      return;
    }

    // Numbered list items
    if (line.match(/^\d+\.\s/)) {
      if (inList && listItems.length > 0 && !line.match(/^\d+\.\s/)) {
        elements.push(
          <ul key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems}
          </ul>
        );
        listItems = [];
      }
      inList = true;
      const content = line.replace(/^\d+\.\s/, '');
      listItems.push(<li key={`li-${lineIndex}`}>{formatInline(content)}</li>);
      return;
    }

    // Flush list before adding regular content
    if (inList && listItems.length > 0) {
      elements.push(
        <ol key={`list-${lineIndex}`} style={{ margin: '8px 0', paddingLeft: '20px' }}>
          {listItems}
        </ol>
      );
      listItems = [];
      inList = false;
    }

    // Regular line
    if (line.trim()) {
      elements.push(
        <div key={`p-${lineIndex}`} style={{ marginBottom: '4px' }}>
          {formatInline(line)}
        </div>
      );
    } else if (lineIndex > 0 && lineIndex < lines.length - 1) {
      // Empty line - add spacing
      elements.push(<div key={`br-${lineIndex}`} style={{ height: '8px' }} />);
    }
  });

  // Flush remaining list items
  if (listItems.length > 0) {
    elements.push(
      <ul key="list-final" style={{ margin: '8px 0', paddingLeft: '20px' }}>
        {listItems}
      </ul>
    );
  }

  return <>{elements}</>;
};

interface ChatPosition {
  x: number;
  y: number;
}

interface ChatSize {
  width: number;
  height: number;
}

interface ChatbotProps {
  orgId: number;
  orgName?: string;
}

const STORAGE_KEY_POSITION = 'chatbox-position';
const STORAGE_KEY_SIZE = 'chatbox-size';
const STORAGE_KEY_OPEN = 'chatbox-open';
const DEFAULT_SIZE = { width: 520, height: 550 };
const MIN_SIZE = { width: 400, height: 400 };
const MAX_SIZE = { width: 800, height: 900 };
const SIDEBAR_WIDTH = 200;

/**
 * Chatbot - Floating AI assistant with conversation history
 * 
 * Features:
 * - Persistent conversations stored in database
 * - Conversation history sidebar
 * - Floating, draggable, resizable chatbox
 * - Handles disconnect gracefully (partial responses saved)
 */
const Chatbot: React.FC<ChatbotProps> = ({ orgId, orgName }) => {
  // Get chat context (page, filters, charts) - may be null if outside provider
  const chatContext = useChatContext();
  
  // Conversation state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  // Message state
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusSteps, setStatusSteps] = useState<Array<{message: string, completed: boolean}>>([]);
  const [streamingContent, setStreamingContent] = useState<string>('');
  
  // Quota state
  const [quotaRefreshTrigger, setQuotaRefreshTrigger] = useState(0);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  
  // Memoized callback to prevent QuotaBar from re-fetching on every render
  const handleQuotaExceeded = useCallback(() => {
    setQuotaExceeded(true);
  }, []);
  
  // UI state
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_OPEN);
    return saved ? JSON.parse(saved) : false;
  });
  
  const { getAccessTokenSilently, isAuthenticated } = useAppAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const shouldAutoScrollRef = useRef<boolean>(true);
  const loadConversationsRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const lastScrollTimeRef = useRef<number>(0);
  const scrollThrottleMs = 400; // Scroll at most every 400ms during streaming

  // Get auth headers
  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (!DEV_BYPASS_AUTH && isAuthenticated) {
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (tokenError) {
        console.warn('Could not get auth token:', tokenError);
      }
    }
    
    return headers;
  }, [getAccessTokenSilently, isAuthenticated]);

  // Load conversations list
  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        org_id: orgId.toString(),
        include_archived: showArchived.toString(),
      });
      const response = await fetch(
        `${API_BASE_URL}/api/conversations?${params}`,
        { headers }
      );
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoadingConversations(false);
    }
  }, [orgId, showArchived, getAuthHeaders]);

  // Load messages for a conversation
  const loadConversation = useCallback(async (conversationId: string) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/api/conversations/${conversationId}`,
        { headers }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(
          (data.messages || []).map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
            is_complete: msg.is_complete,
          }))
        );
        setActiveConversationId(conversationId);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  }, [getAuthHeaders]);

  // Create new conversation (optionally preserving messages for inline creation)
  const createConversation = useCallback(async (preserveMessages: boolean = false): Promise<string | null> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/conversations`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ org_id: orgId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Add to list and select it
        setConversations(prev => [data, ...prev]);
        setActiveConversationId(data.id);
        // Only clear messages if not preserving (e.g., when starting fresh from button)
        if (!preserveMessages) {
          setMessages([]);
        }
        return data.id;
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
    return null;
  }, [orgId, getAuthHeaders]);

  // Archive/unarchive conversation
  const archiveConversation = useCallback(async (conversationId: string, archive: boolean) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/api/conversations/${conversationId}`,
        { 
          method: 'PATCH', 
          headers,
          body: JSON.stringify({ is_archived: archive })
        }
      );
      
      if (response.ok) {
        // Remove from list if archiving and not showing archived
        if (archive && !showArchived) {
          setConversations(prev => prev.filter(c => c.id !== conversationId));
          if (activeConversationId === conversationId) {
            setActiveConversationId(null);
            setMessages([]);
          }
        } else {
          // Update the conversation in the list
          setConversations(prev => prev.map(c => 
            c.id === conversationId ? { ...c, is_archived: archive } : c
          ));
        }
      }
    } catch (error) {
      console.error('Failed to archive conversation:', error);
    }
  }, [activeConversationId, showArchived, getAuthHeaders]);

  // Delete conversation
  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/api/conversations/${conversationId}`,
        { method: 'DELETE', headers }
      );
      
      if (response.ok) {
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        if (activeConversationId === conversationId) {
          setActiveConversationId(null);
          setMessages([]);
        }
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  }, [activeConversationId, getAuthHeaders]);

  // Switch conversation (keep sidebar open for browsing)
  const switchConversation = useCallback(async (conversationId: string) => {
    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setLoading(false);
    setStatusSteps([]);
    setStreamingContent('');
    
    await loadConversation(conversationId);
    // Don't close sidebar - let user browse through conversations
  }, [loadConversation]);

  // Start new chat
  const startNewChat = useCallback(async () => {
    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setLoading(false);
    setStatusSteps([]);
    setStreamingContent('');
    setActiveConversationId(null);
    setMessages([]);
    setShowSidebar(false);
  }, []);

  // Keep ref updated with latest loadConversations (avoids stale closures in timeouts)
  useEffect(() => {
    loadConversationsRef.current = loadConversations;
  }, [loadConversations]);

  // Load conversations when chat opens or org changes
  // Also handles dev bypass mode where isAuthenticated may be false
  useEffect(() => {
    const shouldLoad = isOpen && (isAuthenticated || DEV_BYPASS_AUTH);
    if (shouldLoad) {
      loadConversations();
    }
  }, [isOpen, isAuthenticated, orgId, loadConversations]);

  // Reload conversations when sidebar is opened (in case they weren't loaded yet)
  useEffect(() => {
    if (showSidebar && conversations.length === 0 && !loadingConversations) {
      loadConversations();
    }
  }, [showSidebar, conversations.length, loadingConversations, loadConversations]);

  // Reload when archive filter changes
  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [showArchived]);

  // Check if user is near the bottom of the scroll container
  const isNearBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return true;
    const threshold = 100;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  const handleScroll = useCallback(() => {
    shouldAutoScrollRef.current = isNearBottom();
  }, [isNearBottom]);

  const scrollToBottom = useCallback((force: boolean = false) => {
    if (!shouldAutoScrollRef.current) return;
    
    const now = Date.now();
    // Throttle scrolling during streaming (unless forced)
    if (!force && now - lastScrollTimeRef.current < scrollThrottleMs) {
      return;
    }
    lastScrollTimeRef.current = now;
    
    // Use smooth scroll with a slight delay for better visual tracking
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, []);

  // Scroll on new messages or status changes (not throttled)
  useEffect(() => {
    scrollToBottom(true);
  }, [messages, loading, statusSteps, scrollToBottom]);

  // Throttled scroll during streaming content updates
  useEffect(() => {
    if (streamingContent) {
      scrollToBottom(false); // Throttled
    }
  }, [streamingContent, scrollToBottom]);

  const forceScrollToBottom = useCallback(() => {
    shouldAutoScrollRef.current = true;
    lastScrollTimeRef.current = 0; // Reset throttle
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }, []);

  // Calculate default position
  const getDefaultPosition = (): ChatPosition => {
    const margin = 20;
    return {
      x: window.innerWidth - DEFAULT_SIZE.width - margin,
      y: window.innerHeight - DEFAULT_SIZE.height - margin - 60,
    };
  };

  const [position, setPosition] = useState<ChatPosition>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_POSITION);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return getDefaultPosition();
      }
    }
    return getDefaultPosition();
  });

  const [size, setSize] = useState<ChatSize>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_SIZE);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_SIZE;
      }
    }
    return DEFAULT_SIZE;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_POSITION, JSON.stringify(position));
  }, [position]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SIZE, JSON.stringify(size));
  }, [size]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_OPEN, JSON.stringify(isOpen));
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    // Optimistically add user message to UI
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setStatusSteps([{ message: 'Connecting...', completed: false }]);
    setStreamingContent('');
    forceScrollToBottom();

    // Create abort controller
    abortControllerRef.current = new AbortController();

    try {
      const headers = await getAuthHeaders();
      
      // If no active conversation, create one first (preserve messages since user msg already added)
      let conversationId = activeConversationId;
      if (!conversationId) {
        conversationId = await createConversation(true);  // true = preserve optimistically added message
        if (!conversationId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Use the conversation-aware endpoint with context
      const response = await fetch(
        `${API_BASE_URL}/api/conversations/${conversationId}/stream`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ 
            message: userMessage.content,
            // Include page/filter/chart context for contextual responses
            context: chatContext || undefined
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle quota exceeded (429)
        if (response.status === 429 && errorData.detail?.error === 'quota_exceeded') {
          setQuotaExceeded(true);
          setQuotaRefreshTrigger(prev => prev + 1);
          throw new Error(errorData.detail?.message || 'You\'ve reached your weekly chat limit.');
        }
        
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventStr of events) {
          if (!eventStr.trim()) continue;
          
          const dataMatch = eventStr.match(/^data:\s*(.+)$/m);
          if (!dataMatch) continue;

          try {
            const event: StreamEvent = JSON.parse(dataMatch[1]);
            
            switch (event.type) {
              case 'status':
                // Add new status, mark previous ones as complete
                setStatusSteps(prev => {
                  const newSteps = prev.map(s => ({ ...s, completed: true }));
                  // Only add if different from last step
                  const lastMsg = newSteps[newSteps.length - 1]?.message;
                  if (lastMsg !== event.message) {
                    newSteps.push({ message: event.message || 'Working...', completed: false });
                  }
                  return newSteps;
                });
                break;
              case 'chunk':
                if (event.content) {
                  fullContent += event.content;
                  setStreamingContent(fullContent);
                  // Mark all steps complete when content starts flowing
                  setStatusSteps(prev => prev.map(s => ({ ...s, completed: true })));
                }
                break;
              case 'done':
                break;
              case 'error':
                throw new Error(event.message || 'Unknown error');
            }
          } catch (parseError) {
            console.warn('Failed to parse SSE event:', eventStr, parseError);
          }
        }
      }

      // Add the complete assistant message
      const assistantMessage: Message = {
        role: 'assistant',
        content: fullContent || 'No response received',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Refresh quota bar (usage was tracked on backend)
      setQuotaRefreshTrigger(prev => prev + 1);
      setQuotaExceeded(false);  // Reset in case it was set
      
      // Refresh conversations list to get updated title
      loadConversations();
      
      // Refresh again after delay to catch smart-generated title
      // (LLM title generation takes ~5-8 seconds)
      setTimeout(() => {
        loadConversationsRef.current();
      }, 8000);
      
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Request was cancelled');
        return;
      }
      
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: `❌ Sorry, I couldn't process that request. ${error.message || 'Please try again.'}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setStatusSteps([]);
      setStreamingContent('');
      abortControllerRef.current = null;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  // Format date for sidebar
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    
    // Compare by calendar date in local timezone (handles UTC offset issues)
    const dateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.round((nowLocal.getTime() - dateLocal.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Today';  // Handle same day and future dates (timezone edge cases)
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // FAB button when closed (native button for reliable a11y/automation)
  if (!isOpen) {
    return (
      <Box
        component="button"
        type="button"
        aria-label="Open chat assistant"
        data-testid="chat-open-button"
        onClick={toggleOpen}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          padding: 0,
          background: '#86279C',
          boxShadow: '0 6px 20px rgba(27, 21, 38, 0.28)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 1000,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            background: '#9A35B2',
            boxShadow: '0 8px 24px rgba(27, 21, 38, 0.34)',
          },
          '&:focus-visible': {
            outline: '3px solid rgba(199, 125, 219, 0.6)',
            outlineOffset: 2,
          },
        }}
      >
        <ChatIcon sx={{ color: '#ffffff', fontSize: 28 }} />
      </Box>
    );
  }

  // Full chatbox when open
  return (
    <Rnd
      size={size}
      position={position}
      onDragStop={(e, d) => {
        setPosition({ x: d.x, y: d.y });
      }}
      onResizeStop={(e, direction, ref, delta, newPosition) => {
        setSize({
          width: parseInt(ref.style.width),
          height: parseInt(ref.style.height),
        });
        setPosition(newPosition);
      }}
      minWidth={MIN_SIZE.width}
      minHeight={MIN_SIZE.height}
      maxWidth={MAX_SIZE.width}
      maxHeight={MAX_SIZE.height}
      bounds="window"
      dragHandleClassName="chat-drag-handle"
      style={{
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          background: '#17121F',
          borderRadius: 2,
          boxShadow: '0 20px 60px rgba(199, 125, 219, 0.3), 0 0 0 1px rgba(199, 125, 219, 0.5)',
          overflow: 'hidden',
        }}
      >
        {/* Sidebar */}
        {showSidebar && (
          <Box
            sx={{
              width: SIDEBAR_WIDTH,
              borderRight: '1px solid rgba(199, 125, 219, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              background: 'rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Sidebar Header */}
            <Box
              sx={{
                padding: 1.5,
                borderBottom: '1px solid rgba(199, 125, 219, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>
                {showArchived ? 'Archived' : 'Conversations'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={showArchived ? 'Show active' : 'Show archived'}>
                  <IconButton 
                    size="small" 
                    onClick={() => setShowArchived(!showArchived)} 
                    sx={{ 
                      color: showArchived ? '#C77DDB' : 'rgba(255, 255, 255, 0.5)',
                      '&:hover': { color: '#C77DDB' }
                    }}
                  >
                    <ArchiveIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setShowSidebar(false)} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Search Input */}
            <Box sx={{ padding: 1, borderBottom: '1px solid rgba(199, 125, 219, 0.1)' }}>
              <TextField
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.4)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: '1px solid rgba(199, 125, 219, 0.4)' },
                  },
                  '& .MuiInputBase-input': {
                    color: '#fff',
                    padding: '6px 8px',
                    '&::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
                  },
                }}
              />
            </Box>
            
            {/* Conversations List */}
            <Box sx={{ flex: 1, overflowY: 'auto', padding: 1 }}>
              {loadingConversations ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                  <CircularProgress size={20} sx={{ color: '#C77DDB' }} />
                </Box>
              ) : conversations.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', padding: 1 }}>
                  {showArchived ? 'No archived conversations' : 'No conversations yet'}
                </Typography>
              ) : (
                conversations
                  .filter(conv => 
                    !searchQuery || 
                    (conv.title || '').toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((conv) => (
                    <Box
                      key={conv.id}
                      onClick={() => switchConversation(conv.id)}
                      sx={{
                        padding: '8px 10px',
                        borderRadius: 1,
                        cursor: 'pointer',
                        marginBottom: 0.5,
                        background: activeConversationId === conv.id 
                          ? 'rgba(199, 125, 219, 0.2)' 
                          : 'transparent',
                        '&:hover': {
                          background: 'rgba(199, 125, 219, 0.1)',
                          '& .conv-actions': { opacity: 1 }
                        },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        opacity: conv.is_archived ? 0.6 : 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#fff',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: activeConversationId === conv.id ? 600 : 400,
                          }}
                        >
                          {conv.title || 'New conversation'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.65rem' }}
                        >
                          {formatDate(conv.updated_at)}
                        </Typography>
                      </Box>
                      <Box 
                        className="conv-actions" 
                        sx={{ 
                          display: 'flex', 
                          gap: 0.25,
                          opacity: activeConversationId === conv.id ? 1 : 0,
                          transition: 'opacity 0.15s'
                        }}
                      >
                        <Tooltip title={conv.is_archived ? 'Unarchive' : 'Archive'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              archiveConversation(conv.id, !conv.is_archived);
                            }}
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.3)',
                              padding: '2px',
                              '&:hover': { color: '#C77DDB' }
                            }}
                          >
                            {conv.is_archived ? (
                              <UnarchiveIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ArchiveIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConversation(conv.id);
                            }}
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.3)',
                              padding: '2px',
                              '&:hover': { color: '#ff6b6b' }
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))
              )}
            </Box>
          </Box>
        )}
        
        {/* Main Chat Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Header */}
          <Box
            className="chat-drag-handle"
            sx={{
              padding: 1.5,
              background: 'rgba(134, 39, 156, 0.22)',
              borderBottom: '1px solid rgba(199, 125, 219, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'move',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <DragIndicatorIcon sx={{ color: 'rgba(199, 125, 219, 0.7)', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ color: '#C77DDB', fontWeight: 600 }}>
                💬 IL-2 Assistant
              </Typography>
              {orgName && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', ml: 1 }}>
                  {orgName}
                </Typography>
              )}
              {/* Quota Bar */}
              <Box sx={{ ml: 'auto', mr: 1, width: 80 }}>
                <QuotaBar 
                  compact 
                  refreshTrigger={quotaRefreshTrigger}
                  onQuotaExceeded={handleQuotaExceeded}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="History">
                <IconButton 
                  size="small" 
                  onClick={() => setShowSidebar(!showSidebar)} 
                  sx={{ color: showSidebar ? '#C77DDB' : 'rgba(255, 255, 255, 0.5)' }}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="New Chat">
                <IconButton size="small" onClick={startNewChat} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={toggleOpen} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                <MinimizeIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={toggleOpen} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Chat Messages Area */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'rgba(255, 255, 255, 0.5)',
                  textAlign: 'center',
                  padding: 2,
                  gap: 2,
                }}
              >
                <ChatIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">
                  Hi! I'm your data assistant. Ask me about your brand performance, engagement metrics, or content insights.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {['What\'s performing well?', 'Show me top posts', 'Platform comparison'].map((suggestion) => (
                    <Box
                      key={suggestion}
                      onClick={() => setInput(suggestion)}
                      sx={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        border: '1px solid rgba(199, 125, 219, 0.3)',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#C77DDB',
                          backgroundColor: 'rgba(199, 125, 219, 0.1)',
                        },
                      }}
                    >
                      {suggestion}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              messages.map((message, index) => (
                <Paper
                  key={message.id || index}
                  elevation={0}
                  sx={{
                    padding: 1.5,
                    alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    background:
                      message.role === 'user'
                        ? '#86279C'
                        : 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, marginBottom: 0.5, opacity: 0.8, display: 'block' }}>
                    {message.role === 'user' ? 'You' : 'Assistant'}
                  </Typography>
                  <Box
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      wordBreak: 'break-word',
                      '& ul, & ol': {
                        margin: '8px 0',
                        paddingLeft: '20px',
                      },
                      '& li': {
                        marginBottom: '4px',
                      },
                      '& strong': {
                        fontWeight: 700,
                      },
                      '& em': {
                        fontStyle: 'italic',
                      },
                    }}
                  >
                    {message.role === 'assistant' ? formatMarkdown(message.content) : message.content}
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', marginTop: 0.5 }}>
                    {message.timestamp.toLocaleTimeString()}
                  </Typography>
                </Paper>
              ))
            )}

            {/* Streaming content preview */}
            {loading && streamingContent && (
              <Paper
                elevation={0}
                sx={{
                  padding: 1.5,
                  alignSelf: 'flex-start',
                  maxWidth: '85%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: '#ffffff',
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, marginBottom: 0.5, opacity: 0.8, display: 'block' }}>
                  Assistant
                </Typography>
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    wordBreak: 'break-word',
                  }}
                >
                  {formatMarkdown(streamingContent)}
                </Box>
              </Paper>
            )}

            {/* Status steps indicator */}
            {loading && statusSteps.length > 0 && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 0.5,
                  color: '#C77DDB',
                  padding: '8px 12px',
                  borderRadius: '12px',
                  background: 'rgba(199, 125, 219, 0.08)',
                  alignSelf: 'flex-start',
                  maxWidth: '280px',
                }}
              >
                {statusSteps.map((step, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      opacity: step.completed ? 0.6 : 1,
                      transition: 'opacity 0.3s ease',
                    }}
                  >
                    {step.completed ? (
                      <Box sx={{ 
                        width: 14, 
                        height: 14, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: '#4ade80',
                        fontSize: '12px',
                      }}>
                        ✓
                      </Box>
                    ) : (
                      <CircularProgress size={14} color="inherit" />
                    )}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: step.completed ? 400 : 500,
                        textDecoration: step.completed ? 'none' : 'none',
                      }}
                    >
                      {step.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Quota Exceeded Warning */}
          {quotaExceeded && (
            <Box
              sx={{
                padding: '8px 16px',
                background: 'rgba(255, 68, 68, 0.15)',
                borderTop: '1px solid rgba(255, 68, 68, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: '#ff6b6b' }}>
                ⚠️ You've reached your weekly chat limit. Your quota resets soon.
              </Typography>
            </Box>
          )}

          {/* Input Area */}
          <Box
            sx={{
              padding: 2,
              background: 'rgba(0, 0, 0, 0.5)',
              borderTop: '1px solid rgba(199, 125, 219, 0.2)',
              display: 'flex',
              gap: 1,
            }}
          >
            <form
              onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
              style={{ display: 'flex', gap: '8px', width: '100%' }}
            >
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={quotaExceeded ? "Chat limit reached..." : "Ask about your data..."}
              disabled={loading || quotaExceeded}
              size="small"
              inputProps={{ 'data-testid': 'chat-input' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  // The app theme gives inputs a light surface; the chat panel is always dark.
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  '& fieldset': {
                    borderColor: quotaExceeded ? 'rgba(255, 68, 68, 0.3)' : 'rgba(199, 125, 219, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: quotaExceeded ? 'rgba(255, 68, 68, 0.5)' : 'rgba(199, 125, 219, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: quotaExceeded ? '#ff6b6b' : '#C77DDB',
                  },
                },
                '& .MuiInputBase-input': {
                  color: '#ffffff',
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!input.trim() || loading || quotaExceeded}
              size="small"
              data-testid="chat-send-button"
              sx={{
                backgroundColor: '#C77DDB',
                color: '#1B1526',
                minWidth: '70px',
                pointerEvents: 'auto',
                '&:hover': {
                  backgroundColor: '#D9A3E7',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(199, 125, 219, 0.3)',
                  pointerEvents: 'auto',
                },
              }}
            >
              Send
            </Button>
            </form>
          </Box>
        </Box>
      </Box>
    </Rnd>
  );
};

export default Chatbot;
